import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import Dialog from '../ui/Dialog';
import Field, { FormErrorSummary, focusField } from '../ui/Field';
import PendingDecision from '../ui/PendingDecision';
import type { Ticket } from '../../types';

/**
 * P-06/B (T-06) — "✓ סיום": "מה בוצע"* is required (5+ characters, as the
 * timer's own finish). The server finishes my open work record (and its
 * timer) with the text and completes the task; the progress rolls up (A5).
 */
export default function FinishTaskDialog({ task, onClose, onDone }: { task: Ticket; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(false);
  const check = (v: string) => (v.trim().length < 5 ? t('taskPage.finishSummaryRequired') : undefined);
  const mutation = useMutation({
    mutationFn: () => api.finishTask(task.id, summary.trim()),
    onSuccess: () => { toast('success', t('taskPage.finished', { title: task.title })); onDone(); },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const err = check(summary);
    setFieldError(err);
    setSubmitted(true);
    if (err) { focusField('finish-summary'); return; }
    mutation.mutate();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('taskPage.finishTitle')}
      description={t('taskPage.finishHelp')}
      size="md"
      dismissible={!mutation.isPending}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</button>
          <button type="submit" form="finish-task-form" className="btn btn-primary" disabled={mutation.isPending} aria-busy={mutation.isPending || undefined}>
            <CheckCircle2 size={16} aria-hidden="true" /> {t('taskPage.finishAction')}
          </button>
        </>
      )}
    >
      <form id="finish-task-form" onSubmit={submit} noValidate className="space-y-3">
        {submitted && <FormErrorSummary errors={{ 'finish-summary': fieldError }} labels={{ 'finish-summary': t('taskPage.whatWasDone') }} />}
        {mutation.isError && <p className="text-sm text-danger-700" role="alert">{apiErrorMessage(mutation.error, t('taskPage.finishFailed'))}</p>}
        <div className="relative">
          {/* D-125 PM-03: an automatic (AI) suggestion for "מה בוצע" */}
          <PendingDecision id="PM-03" className="absolute end-0 top-0" />
          <Field label={t('taskPage.whatWasDone')} id="finish-summary" required error={fieldError}>
            <textarea
              className="input min-h-24 text-body-long"
              rows={4}
              value={summary}
              onChange={(e) => { setSummary(e.target.value); if (fieldError) setFieldError(check(e.target.value)); }}
              onBlur={() => { if (summary) setFieldError(check(summary)); }}
            />
          </Field>
        </div>
      </form>
    </Dialog>
  );
}
